import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [tx, setTx] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) { setStatus("no-session"); return; }
    let cancelled = false;
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const r = await api.get(`/payments/status/${sessionId}`);
        if (cancelled) return;
        setTx(r.data);
        if (r.data.payment_status === "paid") { setStatus("paid"); return; }
        if (["failed", "expired"].includes(r.data.payment_status)) { setStatus(r.data.payment_status); return; }
      } catch {
        if (attempts > 30) { setStatus("timeout"); return; }
      }
      if (attempts > 30) { setStatus("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]" data-testid="payment-success-page">
      <div className="card-flat max-w-md w-full p-10 text-center">
        {status === "polling" && (
          <>
            <div className="mx-auto w-14 h-14 rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center animate-pulse">
              <div className="w-3 h-3 rounded-full bg-blue-800" />
            </div>
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-5">Confirming payment…</h1>
            <p className="text-sm text-slate-500 mt-2">We're syncing with Stripe. This takes a couple of seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle size={56} weight="duotone" className="mx-auto text-emerald-600" />
            <h1 className="font-display text-2xl font-bold text-slate-900 mt-4">Payment received</h1>
            <p className="text-sm text-slate-500 mt-2">
              {tx?.kind === "invoice" ? "The invoice is now marked as paid." : "Thanks — the payment is processed."}
            </p>
            <div className="mt-6 flex gap-2 justify-center">
              <Button onClick={() => navigate("/invoices")} className="bg-[#1E3A8A] hover:bg-[#1E40AF] text-white" data-testid="go-invoices-btn">
                View invoices <ArrowRight size={14} className="ml-1.5" />
              </Button>
              <Button variant="outline" onClick={() => navigate("/dashboard")}>Dashboard</Button>
            </div>
          </>
        )}
        {(status === "failed" || status === "expired" || status === "timeout" || status === "no-session") && (
          <>
            <h1 className="font-display text-2xl font-bold text-slate-900">Something went wrong</h1>
            <p className="text-sm text-slate-500 mt-2">Status: {status}. Please try again or contact your admin.</p>
            <Button onClick={() => navigate("/invoices")} className="mt-6 bg-[#1E3A8A] hover:bg-[#1E40AF] text-white">Back to invoices</Button>
          </>
        )}
      </div>
    </div>
  );
}
